/**
 * Service de reserva.
 *
 * Encapsula la construcción del FormData y la llamada HTTP. El hook no
 * necesita saber nada sobre la forma del payload.
 */

import { postApi } from 'views/services/api';
import { hotelInputToUTC } from 'utils/timeZone';
import { STAY_TYPE_NIGHT } from '../constants';
import { toCents, formatAmount } from '../utils/money';

/**
 * Conversor de fecha de input a string UTC para el backend.
 *
 * Usamos `hotelInputToUTC` por defecto (consciente de la zona horaria del
 * hotel). El parámetro `toUTCString` en `buildReservationFormData` permite
 * inyectar otro conversor en flujos legacy que aún no usan TZ del hotel.
 */
const defaultToUTCString = (localDateStr) => hotelInputToUTC(localDateStr);

/**
 * Construye el FormData que el endpoint `api/customer/doreservation` espera.
 *
 * Importante: TODOS los montos pasan por `toCents` + `formatAmount` para
 * garantizar que el backend reciba siempre "10.00" cuando el usuario tipea
 * 10, sin importar el camino de cálculo intermedio.
 *
 * @param {object} params
 * @param {object} params.values - Formik values
 * @param {object} params.hotel - { hotelId, _id }
 * @param {boolean} [params.showSplitPayments=false]
 * @param {Array}   [params.splitPayments=[]]
 * @param {object|null} [params.promoCode=null]
 * @param {number}  [params.discountCents=0]
 * @param {string}  [params.cashRegisterId] - caja activa, solo para flujo desde dashboard
 * @param {function} [params.toUTCString] - conversor de fechas custom
 * @returns {FormData}
 */
export const buildReservationFormData = ({
  values,
  hotel,
  showSplitPayments = false,
  splitPayments = [],
  promoCode = null,
  discountCents = 0,
  cashRegisterId,
  toUTCString = defaultToUTCString
}) => {
  const formData = new FormData();

  formData.append('hotelId', hotel.hotelId);
  formData.append('createdBy', hotel._id);
  formData.append('roomNo', values.roomNo);
  formData.append('checkInDate', toUTCString(values.checkInDate));
  formData.append('checkOutDate', toUTCString(values.checkOutDate));
  formData.append('stayType', values.stayType || STAY_TYPE_NIGHT);

  if (showSplitPayments && splitPayments.length > 0) {
    formData.append('splitPayments', JSON.stringify(splitPayments));
  } else {
    formData.append('paymentMethod', values.paymentMethod || '');
    formData.append('paymentCurrency', values.paymentCurrency || '');
    formData.append('exchangeRateUsed', values.exchangeRateUsed || '');
    formData.append('amountInForeignCurrency', values.amountInForeignCurrency || '');
    formData.append('batchNumber', values.batchNumber || '');
    formData.append('transactionNumber', values.transactionNumber || '');
  }

  // Bug fix histórico: los montos pasaban por parseFloat/toFixed en distintas
  // capas. Normalizamos siempre vía centavos para evitar el caso 10 -> 9.
  formData.append('advanceAmount', formatAmount(toCents(values.advanceAmount)));
  formData.append('totalAmount', formatAmount(toCents(values.totalAmount)));

  formData.append('customers', JSON.stringify(values.customers));

  if (promoCode) {
    formData.append('promoCodeId', promoCode._id);
    formData.append('discountAmount', formatAmount(discountCents));
  }

  // Solo el flujo desde el dashboard captura la caja activa.
  if (cashRegisterId) {
    formData.append('cashRegisterId', cashRegisterId);
  }

  values.customers.forEach((customer) => {
    if (customer.idFile instanceof File) {
      formData.append('idFile', customer.idFile, customer.idFile.name);
    }
  });

  return formData;
};

/**
 * POST de la reserva. Devuelve la respuesta tal cual para que el caller
 * decida qué hacer con ella (toast de éxito/error, navegación, etc.).
 *
 * @param {FormData} formData
 * @returns {Promise<object>}
 */
export const submitReservation = (formData) =>
  postApi('api/customer/doreservation', formData);
